import React, { useCallback, useMemo, useState } from 'react';
import './entities-list-row.scss';
import { IconButton as MuiButton } from '@mui/material';
import LinkWrapper from '@components/LinkWrapper/LinkWrapper';
import classNames from 'classnames';
import { BaseEntityNameLess } from '@api/entities/BaseEntity';
import DraggableItem from '@components/DraggableItem/DraggableItem';
import ActionMenu, { MenuActionType } from '@components/ActionMenu/ActionMenu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { themeColors } from '@styles/theme';
import { useTheme } from '@context/themeContext';
import DeleteListEntityActionFabric from '../../../delete-list-entity-action/DeleteListEntityAction';
import { useTranslation } from 'react-i18next';

export type EntitiesListRowProps<T extends BaseEntityNameLess> = {
    entity: T;
    rowNumber?: number | null;
    deleteFn: (id: string) => Promise<void>;
    form?: any; // fix type
    selected?: boolean;
    getUrlFunc?: (entity: T) => string;
    onRowClick: (entity: T) => Promise<void>;
    hasLink?: boolean;
    menuActions?: MenuActionType[];
    loadDataFn?: () => Promise<void>;
    style?: any;
    attributes?: any;
    listeners?: any;
};

export const EntitiesListRow = React.memo(
    React.forwardRef(
        <T extends BaseEntityNameLess>(
            {
                entity,
                rowNumber,
                deleteFn,
                form: Form,
                selected,
                getUrlFunc,
                onRowClick,
                hasLink = true,
                menuActions,
                loadDataFn,
                attributes,
                listeners,
                ...rest
            }: EntitiesListRowProps<T>,
            ref: React.ForwardedRef<HTMLDivElement>
        ) => {
            const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
            const { theme } = useTheme();
            const { t } = useTranslation();

            const color = theme === 'light' ? themeColors.navyBlue : themeColors.white;

            const classes = classNames(
                'entities-list-row',
                selected && 'entities-list-row_selected'
            );

            const onDeleteHandler = useCallback(async () => {
                if (entity.id) {
                    await deleteFn?.(entity.id);
                }
            }, [entity, deleteFn]);

            const menuActionsWithDelete = useMemo(() => {
                return (menuActions ?? [])?.concat({
                    label: 'Delete',
                    dialogTitle: t('common.delete-confirmation'),
                    dialogContent: DeleteListEntityActionFabric({
                        id: entity.id,
                        loadEntitiesFn: loadDataFn!,
                        deleteEntityFn: onDeleteHandler,
                        entity,
                    }),
                });
            }, [menuActions]);

            const handleActionClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
                event.stopPropagation(); // select isn't closed
                setMenuAnchor(event.currentTarget);
            }, []);

            const handleMenuClose = useCallback(
                (event?: React.MouseEvent | React.KeyboardEvent) => {
                    const modal = document.querySelector('.MuiDialog-root');
                    if (modal && modal.contains(event?.target as Node)) {
                        return;
                    }
                    setMenuAnchor(null);
                },
                []
            );

            return (
                <div
                    className={classes}
                    data-testid={`entitiesListRow_${entity.id}`}
                    ref={ref}
                    {...attributes}
                    {...listeners}
                    {...rest}
                >
                    <div
                        className={`entities-list-row__link-wrapper`}
                        onClick={() => onRowClick(entity)}
                    >
                        <LinkWrapper
                            to={getUrlFunc?.(entity) ?? `./${entity.id}/`}
                            hasLink={hasLink}
                            className={`entities-list-row__link`}
                        >
                            {rowNumber !== null ? (
                                <div className="entities-list-row__number">{rowNumber}</div>
                            ) : null}
                            <div className={`entities-list-row__fields`}>
                                {Form ? <Form entity={entity} /> : entity.id}
                            </div>
                        </LinkWrapper>
                    </div>
                    <div className="entities-list-row__actions">
                        <MuiButton size="small" onClick={handleActionClick}>
                            <MoreVertIcon fontSize="small" sx={{ color }} />
                        </MuiButton>
                        <ActionMenu
                            anchorEl={menuAnchor}
                            actions={menuActionsWithDelete}
                            onClose={handleMenuClose as any}
                        />
                    </div>
                </div>
            );
        }
    )
);

export function EntitiesListItem<T extends BaseEntityNameLess>(
    props: EntitiesListRowProps<T> & {
        draggable?: boolean;
    }
) {
    if (props.draggable) {
        return (
            <DraggableItem<EntitiesListRowProps<T>>
                id={props.entity.id}
                Component={EntitiesListRow as any}
                props={props}
            />
        );
    }

    return <EntitiesListRow {...(props as any)} />;
}
